"use client";

import * as Sentry from "@sentry/nextjs";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState, useTransition } from "react";
import publishEventToSDK from "./publish-event-to-sdk";

interface UseSdkNavigationOptions {
  targetRoute: string;
}

/**
 * Hook for handling navigation with SDK event publishing and smooth transitions.
 * Checks session status to determine the appropriate route.
 *
 * @param options - Navigation options
 * @param options.targetRoute - Route to navigate to next
 */
export const useSdkNavigation = ({ targetRoute }: UseSdkNavigationOptions) => {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [isOnDevice, setIsOnDevice] = useState(false);

  // Check if running on device (must be in useEffect to avoid SSR issues)
  useEffect(() => {
    setIsOnDevice(
      window.navigator.userAgent.includes("iPhone") ||
        window.navigator.userAgent.includes("Android")
    );
  }, []);

  // Navigation helper
  const navigateToRoute = useCallback(
    (route: string) => {
      Sentry.addBreadcrumb({
        category: "sdk-navigation",
        message: "Navigating to route",
        level: "info",
        data: { route, isOnDevice },
      });

      // For SDK navigation, ensure the route is not URL-encoded
      // The SDK/native app should handle the route as-is
      publishEventToSDK({ topic: "navigate", data: route });
      if (!isOnDevice) {
        startTransition(() => {
          router.push(route);
        });
      }
    },
    [router, isOnDevice]
  );

  const onClick = useCallback(async () => {
    Sentry.addBreadcrumb({
      category: "sdk-navigation",
      message: "onClick triggered",
      level: "info",
      data: { targetRoute },
    });

    // Use determined route
    navigateToRoute(targetRoute);
  }, [targetRoute, navigateToRoute]);

  return { onClick, isPending };
};
